import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  EMPTY,
  Subscription,
  from,
  Observable,
  of,
  Subject,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  retryWhen,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnSessionService } from '@lib/auth';
import { SnCurrentOrganization } from '@lib/auth/services/current-organization.service';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { t } from '@lib/translation';
import { SnAblyService } from '@lib/transport/ably';
import { EventTypes, FsAzurePubSubService } from '@lib/transport/azure';

import { LiveUpdateServiceType } from './live-update-switch.types';

export interface SubscribeCallbackParams {
  name: string;
  data: any;
  extras?: any;
}

export interface SubscribeToChannelParams {
  channel: string;
  callback: (message: SubscribeCallbackParams) => void;
  queryFilter?: {
    key: string;
    value: string;
  };
  filter?: (message: { name: string; data: any }) => boolean;
  withHandle?: boolean;
  joinStrategy?: 'none' | 'auto';
}

@Injectable({ providedIn: 'root' })
export class FsLiveUpdateSwitchService {
  private readonly MAX_RETRIES = 5;
  private readonly RETRY_DELAY = 3000;

  private readonly _useAbly: boolean = true;
  private readonly _useAzurePubSub: boolean = false;
  private readonly _useShadowMode: boolean = false;

  private _channels: Map<string, string> = new Map();
  private _subscriptionRegistry: Map<
    string,
    {
      channel: string;
      handler?: (message: SubscribeCallbackParams) => void;
      derived?: boolean;
      derivedChannel?: any;
      azureGroup?: string;
      azureSubscription?: Subscription;
    }
  > = new Map();

  private _initAbly$: Observable<void>;
  private _initAzure$: Observable<void>;
  private readonly _destroy$ = new Subject<void>();

  private _activeLiveUpdateService$ = new BehaviorSubject<LiveUpdateServiceType>(
    LiveUpdateServiceType.ABLY,
  );

  public get activeLiveUpdateService(): Observable<LiveUpdateServiceType> {
    return this._activeLiveUpdateService$.asObservable();
  }

  constructor(
    private readonly _azurePubSubService: FsAzurePubSubService,
    private readonly _ablyService: SnAblyService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _sessionService: SnSessionService,
    private readonly _toastrService: ToastrService,
  ) {
    const liveUpdateService = this._currentOrganization?.settings.azurePubSubEnableModeWeb;
    if (liveUpdateService) {
      this._useAbly = liveUpdateService === LiveUpdateServiceType.ABLY;
      this._useAzurePubSub = liveUpdateService === LiveUpdateServiceType.AZURE_PUBSUB;
      this._useShadowMode = liveUpdateService === LiveUpdateServiceType.SHADOW;
    }

    this._sessionService.loggedIn$.pipe(takeUntil(this._destroy$)).subscribe((loggedIn) => {
      if (!loggedIn) {
        void this.destroy();
        this._initAbly$ = null;
        this._initAzure$ = null;
      }
    });

    this.subscribeToChannel = this.subscribeToChannel.bind(this);
    this.initLiveTracking = this.initLiveTracking.bind(this);
    this.onReconnect = this.onReconnect.bind(this);
  }

  public initLiveTracking(): Promise<void> {
    this._ensureInitObservables();

    if (this._useShadowMode) {
      if (this._initAzure$) {
        this._initAzure$.subscribe({
          error: (error) => {
            console.warn('Azure PubSub initialization failed in shadow mode:', error);
          },
        });
      }

      return this._initAbly$.pipe(catchError((error) => of(undefined))).toPromise<void>();
    }

    const initObservable = this._useAbly ? this._initAbly$ : this._initAzure$;
    return initObservable.pipe(catchError((error) => of(undefined))).toPromise<void>();
  }

  public async subscribeToChannel(params: SubscribeToChannelParams): Promise<void | string> {
    if (params?.withHandle) {
      return await this._subscribeWithHandle(params);
    }
    const { queryFilter, channel, filter, callback } = params ?? {};

    if (this._channels.has(channel)) {
      return;
    }

    this._channels.set(channel, crypto.randomUUID());

    if (this._useShadowMode) {
      await this.initLiveTracking();
      if (this._initAzure$) {
        this._initAzure$.subscribe({
          error: (error) => {
            console.warn(
              'Azure PubSub initialization failed in shadow mode during subscription:',
              error,
            );
          },
        });
      }
    } else if (this._useAzurePubSub) {
      this._ensureInitObservables();
      await this._initAzure$.pipe(catchError(() => of(undefined))).toPromise();
    } else if (this._useAbly) {
      this._ensureInitObservables();
      await this._initAbly$.pipe(catchError(() => of(undefined))).toPromise();
    }

    if (this._useAzurePubSub || this._useShadowMode) {
      const groupName = params.queryFilter
        ? `${channel.replace(/:/g, '.')}.${queryFilter.key}.${queryFilter.value}`
        : channel.replace(/:/g, '.');

      this._azurePubSubService.sendEvent(EventTypes.JoinChannel, groupName).catch((error) => {
        if (error?.message === 'Client not initialized') {
          console.warn(
            'Azure PubSub client not initialized during channel subscription:',
            error.message,
          );
          return;
        }
        console.warn('Azure PubSub error during channel subscription:', error.message);
      });

      this._azurePubSubService.onMessage(groupName, filter).subscribe((message) => {
        if (!this._useShadowMode) {
          callback(message);
        }
      });
    }

    if (this._useAbly || this._useShadowMode) {
      try {
        if (queryFilter) {
          const derivedChannel = this._ablyService.ablyClient.chanels.getDerived(channel, {
            filter: `${queryFilter.key} == \`"${queryFilter.value}"\``,
          });
          derivedChannel.subscribe((message) => {
            if (!filter || filter(message)) {
              this._ablyService.trackMessage(message, channel);
              callback(message);
            }
          });
        } else {
          this._ablyService.ablyClient.chanels.get(channel).subscribe((message) => {
            if (!filter || filter(message)) {
              this._ablyService.trackMessage(message, channel);
              callback(message);
            }
          });
        }
      } catch (error) {
        if (this._useShadowMode) {
          const fallbackGroupName = params.queryFilter
            ? `${channel.replace(/:/g, '.')}.${queryFilter.key}.${queryFilter.value}`
            : channel.replace(/:/g, '.');
          this._azurePubSubService.onMessage(fallbackGroupName, filter).subscribe((message) => {
            callback(message);
          });
        }
      }
    }
  }

  /**
   * Subscribe to a channel and return a subscription id for precise unsubscription later.
   * This attaches only a single handler and records it, so we can remove just this handler later.
   */
  private async _subscribeWithHandle(params: SubscribeToChannelParams): Promise<string> {
    const { queryFilter, channel, filter, callback, joinStrategy } = params ?? {};
    await this.initLiveTracking();
    const subscriptionId = crypto.randomUUID();
    const groupName = queryFilter
      ? `${channel.replace(/:/g, '.')}.${queryFilter.key}.${queryFilter.value}`
      : channel.replace(/:/g, '.');

    if (this._useAzurePubSub || this._useShadowMode) {
      if (joinStrategy === 'auto') {
        try {
          await this._azurePubSubService.sendEvent(EventTypes.JoinChannel, groupName);
        } catch (error) {
          if (error?.message !== 'Client not initialized') {
            throw error;
          }
        }
      }

      const azureSub = this._azurePubSubService
        .onMessage(groupName, filter)
        .subscribe((message) => {
          if (!this._useShadowMode) {
            callback(message);
          }
        });

      this._subscriptionRegistry.set(subscriptionId, {
        channel,
        azureGroup: groupName,
        azureSubscription: azureSub,
      });
      return subscriptionId;
    }

    if (this._useAbly || this._useShadowMode) {
      try {
        const handler = (message: any) => {
          if (!filter || filter(message)) {
            this._ablyService.trackMessage(message, channel);
            callback(message);
          }
        };

        if (queryFilter) {
          const derivedChannel = this._ablyService.ablyClient.chanels.getDerived(channel, {
            filter: `${queryFilter.key} == \`"${queryFilter.value}"\``,
          });
          derivedChannel.subscribe(handler);
          this._subscriptionRegistry.set(subscriptionId, {
            channel,
            handler,
            derived: true,
            derivedChannel,
          });
        } else {
          const ablyChannel = this._ablyService.ablyClient.chanels.get(channel);
          ablyChannel.subscribe(handler);
          this._subscriptionRegistry.set(subscriptionId, { channel, handler });
        }
        return subscriptionId;
      } catch (error) {
        if (this._useShadowMode) {
          const azureSub = this._azurePubSubService
            .onMessage(groupName, filter)
            .subscribe((message) => {
              callback(message);
            });
          this._subscriptionRegistry.set(subscriptionId, {
            channel,
            azureGroup: groupName,
            azureSubscription: azureSub,
          });
          return subscriptionId;
        }
        throw error;
      }
    }

    return subscriptionId;
  }

  /**
   * Precisely unsubscribe a single subscription by id, without affecting other subscribers on the same channel.
   */
  public async unsubscribeById(subscriptionId: string): Promise<void> {
    const entry = this._subscriptionRegistry.get(subscriptionId);
    if (!entry) {
      return;
    }

    // Azure path
    if (entry.azureSubscription) {
      try {
        entry.azureSubscription.unsubscribe();
      } catch (error) {
        console.warn('Azure subscription unsubscribe failed:', error);
      }
    }

    // Ably path
    if (entry.handler) {
      try {
        if (entry.derived && entry.derivedChannel) {
          entry.derivedChannel.unsubscribe(entry.handler);
        } else {
          const ablyChannel = this._ablyService.ablyClient?.chanels.get(entry.channel);
          ablyChannel?.unsubscribe(entry.handler);
        }
      } catch (error) {
        console.warn('Ably channel unsubscribe failed:', error);
      }
    }

    this._subscriptionRegistry.delete(subscriptionId);
  }

  public async unsubscribeFromChannel(channel: string): Promise<void> {
    if (this._useAzurePubSub || this._useShadowMode) {
      const groupName = channel.replace(/:/g, '.');
      try {
        await this._azurePubSubService.sendEvent(EventTypes.LeaveChannel, groupName);
      } catch (error) {
        if (error?.message === 'Client not initialized') {
          console.warn(
            'Azure PubSub client not initialized during channel unsubscribe operation:',
            error.message,
          );
          return;
        }
        throw error;
      }
    }

    if (this._useAbly || this._useShadowMode) {
      try {
        const ablyChannel = this._ablyService.ablyClient?.chanels.get(channel);
        if (ablyChannel) {
          ablyChannel.unsubscribe();
          if (ablyChannel['subscriptions']?.any?.length < 1) {
            ablyChannel.detach();
          }
        }
      } catch (error) {
        console.warn('Ably channel unsubscribe failed for channel:', channel, error);
      }
    }
    this._channels.delete(channel);
  }

  public handleLayerSubscription = async ({
    layerId,
    action,
    organizationId,
    callback,
  }: {
    layerId: string;
    action: EventTypes;
    organizationId: string;
    callback?: (message: { name: string; data: any }) => void;
  }): Promise<void> => {
    if (this._useShadowMode) {
      await this.initLiveTracking();
    } else if (this._useAzurePubSub) {
      this._ensureInitObservables();
      await this._initAzure$.pipe(catchError(() => of(undefined))).toPromise();
    } else if (this._useAbly) {
      this._ensureInitObservables();
      await this._initAbly$.pipe(catchError(() => of(undefined))).toPromise();
    }

    if (!layerId) {
      throw new Error('Layer ID is required');
    }

    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    this._channels.set(`${organizationId}.layers.${layerId}`, crypto.randomUUID());

    if (this._useAzurePubSub || this._useShadowMode) {
      const channelName = `${organizationId}.layers.${layerId}`;
      try {
        await this._azurePubSubService.sendEvent(action, channelName);

        if (action === EventTypes.JoinChannel && callback && !this._useShadowMode) {
          this._azurePubSubService.onMessage(channelName).subscribe((message) => {
            callback(message);
          });
        }
      } catch (error) {
        if (action === EventTypes.LeaveChannel && error?.message === 'Client not initialized') {
          console.warn(
            'Azure PubSub client not initialized during channel leave operation:',
            error.message,
          );
          return;
        }
        throw error;
      }
    }

    if (this._useAbly || this._useShadowMode) {
      const channel = `${organizationId}:layers`;
      const ablyChannel = this._ablyService.ablyClient.chanels.get(channel);

      if (action === EventTypes.JoinChannel) {
        if (ablyChannel) {
          ablyChannel.attach();
          if (callback) {
            const derivedChannel = this._ablyService.ablyClient.chanels.getDerived(channel, {
              filter: `headers.layer_id == \`"${layerId}"\``,
            });
            derivedChannel.subscribe(callback);
          }
        }
      } else if (action === EventTypes.LeaveChannel) {
        if (ablyChannel) {
          if (callback) {
            ablyChannel.unsubscribe(callback);
          }
          ablyChannel.detach();
        }
      }
    }
  };

  public async destroy(): Promise<void> {
    await Promise.all(
      Array.from(this._channels.keys()).map(async (channel) => {
        try {
          await this.unsubscribeFromChannel(channel);
        } catch (error) {
          console.warn('Failed to unsubscribe from channel during destroy:', channel, error);
        }
      }),
    );
    this._channels.clear();
    this._destroy$.next();
    this._destroy$.complete();
    this._ablyService.destroyAblyClient();
    this._azurePubSubService.destroy();
    this._initAbly$ = null;
    this._initAzure$ = null;
  }

  public reset(): void {
    this._azurePubSubService.reset();
    this._channels.clear();
    this._initAbly$ = null;
    this._initAzure$ = null;
  }

  public onReconnect<T>(action: () => Observable<T>): Observable<T> {
    return new Observable((subscriber) => {
      this.initLiveTracking()
        .then(() => {
          const reconnect$ = this._useAzurePubSub
            ? this._azurePubSubService.onReconnect()
            : this._ablyService.ablyClient?.reconnected$;

          if (!reconnect$) {
            subscriber.error(new Error('No reconnect stream available'));
            return;
          }

          reconnect$.pipe(switchMap(() => action())).subscribe({
            next: (value) => subscriber.next(value),
            error: (err) => subscriber.error(err),
            complete: () => subscriber.complete(),
          });
        })
        .catch((error) => {
          subscriber.error(error);
        });
    });
  }

  private _setActiveLiveUpdateService(source: LiveUpdateServiceType): void {
    this._activeLiveUpdateService$.next(source);
  }

  private _ensureInitObservables(): void {
    if ((this._useAbly || this._useShadowMode) && !this._initAbly$) {
      this._initAbly$ = this._createAblyInit$();
    }

    if ((this._useAzurePubSub || this._useShadowMode) && !this._initAzure$) {
      this._initAzure$ = this._createAzureInit$();
    }
  }

  private _createAblyInit$(): Observable<void> {
    if (!this._ablyService.isAblyAvailable) {
      return of(undefined);
    }

    const organizationId = this._currentOrganization?.id;

    return this._sessionService.getAblyStorageKey().pipe(
      switchMap((token) => {
        if (!token) {
          return this._sessionService.getAblyToken().pipe(map((res) => res.token));
        }
        return of(token);
      }),
      map((token) => {
        this._ablyService.initAblyClient(token, organizationId);
        return undefined;
      }),
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= this.MAX_RETRIES) {
              return throwError(error);
            }
            return timer(this.RETRY_DELAY);
          }),
        ),
      ),
      shareReplay(1),
      catchError((error) => {
        console.error('Ably initialization failed after retries:', error);
        return throwError(error);
      }),
    );
  }

  private _createAzureInit$(): Observable<void> {
    const azureUrl = localStorage.getItem('azureWebpubsubUrl');
    const organizationId = this._currentOrganization?.id;

    const initObservable = azureUrl
      ? from(this._azurePubSubService.initAzurePubSubClient(azureUrl, organizationId))
      : this._sessionService
          .getAzureWebPubSubUrl()
          .pipe(
            switchMap((url) =>
              from(this._azurePubSubService.initAzurePubSubClient(url, organizationId)),
            ),
          );

    return initObservable.pipe(
      map(() => {}),
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, retryCount) => {
            if (retryCount >= this.MAX_RETRIES) {
              if (this._useShadowMode) {
                // In shadow mode, log the failure but don't throw
                this._datadogService.addAction('azure_pubsub_client_connection_failure', {
                  error: error.message || JSON.stringify(error),
                  organizationId: organizationId,
                });
                return EMPTY;
              }
              return throwError(error);
            }
            return timer(this.RETRY_DELAY);
          }),
        ),
      ),
      shareReplay(1),
      catchError((error) => {
        if (this._useShadowMode) {
          // In shadow mode, Azure failures are non-blocking
          console.warn(
            'Azure PubSub initialization failed in shadow mode:',
            error.message || JSON.stringify(error),
          );
          return EMPTY;
        }
        this._toastrService.error(t('transport.liveUpdateSwitch.azurePubSubInitializationFailed'));
        console.error(
          'Azure PubSub initialization failed:',
          error.message || JSON.stringify(error),
        );
        return throwError(error);
      }),
    );
  }
}
